import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { getAdminPayments } from "@/lib/admin-payments.functions";

export const Route = createFileRoute("/admin/payments")({
  head: () => ({ meta: [{ title: "Audit paiements — Admin" }] }),
  component: AdminPaymentsPage,
});

function StatusPill({ status }: { status: string | null }) {
  const s = (status ?? "").toLowerCase();
  const color =
    s === "active" || s === "trialing" || s === "paid"
      ? "bg-green-500/15 text-green-700 dark:text-green-300"
      : s === "past_due" || s === "incomplete" || s === "open"
        ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300"
        : s === "canceled" || s === "unpaid" || s === "void"
          ? "bg-red-500/15 text-red-700 dark:text-red-300"
          : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${color}`}>
      {status ?? "—"}
    </span>
  );
}

function EnvPill({ env }: { env: string }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
        env === "live" ? "bg-accent text-accent-foreground" : "bg-muted text-foreground"
      }`}
    >
      {env}
    </span>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function AdminPaymentsPage() {
  const nav = useNavigate();
  const fn = useServerFn(getAdminPayments);
  const [authed, setAuthed] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav({ to: "/login" });
      else setAuthed(true);
    });
  }, [nav]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-payments"],
    queryFn: () => fn(),
    enabled: authed,
  });

  const filteredSubs = useMemo(() => {
    if (!data?.subscriptions) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.subscriptions;
    return data.subscriptions.filter(
      (s) =>
        s.user_email?.toLowerCase().includes(q) ||
        s.user_id.toLowerCase().includes(q) ||
        s.stripe_customer_id.toLowerCase().includes(q) ||
        s.stripe_subscription_id.toLowerCase().includes(q) ||
        (s.price_id ?? "").toLowerCase().includes(q),
    );
  }, [data, filter]);

  const filteredInvoices = useMemo(() => {
    if (!data?.invoices) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.invoices;
    return data.invoices.filter(
      (i) =>
        i.customer_email?.toLowerCase().includes(q) ||
        i.customer_id.toLowerCase().includes(q) ||
        i.id.toLowerCase().includes(q) ||
        (i.user_id ?? "").toLowerCase().includes(q),
    );
  }, [data, filter]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1
              className="font-display font-bold text-3xl tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Audit paiements
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Abonnements (DB) et factures (Stripe sandbox + live).
            </p>
          </div>
          <div className="flex gap-2">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrer (email, user id, customer…)"
              className="px-3 py-2 rounded-xl border border-foreground/15 bg-card text-sm w-72 max-w-full"
            />
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="bg-foreground text-background px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isFetching ? "…" : "Rafraîchir"}
            </button>
          </div>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Chargement…</p>}
        {error && (
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : "Erreur"}
          </p>
        )}

        {data && !data.is_admin && (
          <div className="bg-card border border-foreground/10 rounded-2xl p-6">
            <p className="font-semibold mb-2">Accès refusé</p>
            <p className="text-sm text-muted-foreground">
              Votre compte n'est pas dans la liste des administrateurs autorisés. Ajoutez
              votre email au secret serveur <code>ADMIN_EMAILS</code> (séparés par des
              virgules) puis rechargez.
            </p>
            {data.errors.length > 0 && (
              <ul className="mt-3 text-xs text-muted-foreground list-disc list-inside">
                {data.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {data?.is_admin && (
          <>
            {data.errors.length > 0 && (
              <div className="mb-4 p-3 rounded-xl border border-yellow-500/40 bg-yellow-500/5 text-xs">
                {data.errors.map((e, i) => (
                  <p key={i}>⚠️ {e}</p>
                ))}
              </div>
            )}

            <section className="mb-10">
              <h2 className="font-bold text-lg mb-3">
                Abonnements ({filteredSubs.length})
              </h2>
              <div className="overflow-x-auto border border-foreground/10 rounded-2xl">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3">Env</th>
                      <th className="p-3">Statut</th>
                      <th className="p-3">Utilisateur</th>
                      <th className="p-3">Plan</th>
                      <th className="p-3">Période fin</th>
                      <th className="p-3">Annulation</th>
                      <th className="p-3">MAJ</th>
                      <th className="p-3">Stripe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubs.map((s) => (
                      <tr key={s.id} className="border-t border-foreground/10 align-top">
                        <td className="p-3"><EnvPill env={s.environment} /></td>
                        <td className="p-3"><StatusPill status={s.status} /></td>
                        <td className="p-3">
                          <div className="font-medium">{s.user_email ?? "—"}</div>
                          <div className="text-muted-foreground font-mono text-[10px]">
                            {s.user_id.slice(0, 8)}…
                          </div>
                        </td>
                        <td className="p-3 font-mono">{s.price_id ?? "—"}</td>
                        <td className="p-3">{fmtDate(s.current_period_end)}</td>
                        <td className="p-3">
                          {s.cancel_at_period_end ? "à terme" : "non"}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {fmtDate(s.updated_at)}
                        </td>
                        <td className="p-3 font-mono text-[10px] text-muted-foreground">
                          {s.stripe_subscription_id.slice(0, 14)}…
                        </td>
                      </tr>
                    ))}
                    {filteredSubs.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-6 text-center text-muted-foreground">
                          Aucun abonnement.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-bold text-lg mb-3">
                Factures ({filteredInvoices.length})
              </h2>
              <div className="overflow-x-auto border border-foreground/10 rounded-2xl">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3">Env</th>
                      <th className="p-3">Statut</th>
                      <th className="p-3">Date</th>
                      <th className="p-3">Montant</th>
                      <th className="p-3">Client</th>
                      <th className="p-3">User id</th>
                      <th className="p-3">Liens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((i) => (
                      <tr key={`${i.environment}-${i.id}`} className="border-t border-foreground/10 align-top">
                        <td className="p-3"><EnvPill env={i.environment} /></td>
                        <td className="p-3"><StatusPill status={i.status} /></td>
                        <td className="p-3">{fmtDate(i.created)}</td>
                        <td className="p-3 font-mono">
                          {i.amount_paid.toFixed(2)} {i.currency.toUpperCase()}
                        </td>
                        <td className="p-3">
                          <div>{i.customer_email ?? "—"}</div>
                          <div className="text-muted-foreground font-mono text-[10px]">
                            {i.customer_id.slice(0, 14)}…
                          </div>
                        </td>
                        <td className="p-3 font-mono text-[10px] text-muted-foreground">
                          {i.user_id ? `${i.user_id.slice(0, 8)}…` : "—"}
                        </td>
                        <td className="p-3 space-x-2">
                          {i.hosted_invoice_url && (
                            <a
                              href={i.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              Voir
                            </a>
                          )}
                          {i.pdf_url && (
                            <a
                              href={i.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredInvoices.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-muted-foreground">
                          Aucune facture.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        <Link
          to="/billing"
          className="block text-center text-sm text-muted-foreground hover:text-foreground mt-10"
        >
          ← Retour à la facturation
        </Link>
      </div>
    </div>
  );
}